<template>
  <div
    class="upsell-upgrade-cta flex items-center justify-between"
    data-testid="upsell-upgrade-cta"
  >
    <div class="upsell-upgrade-cta__pricing flex flex-col items-start">
      <div class="flex items-baseline">
        <OwnType
          class="upsell-upgrade-cta__price"
          :text="price"
          variant="title-regular"
        />
        <OwnType
          class="upsell-upgrade-cta__cadence"
          :text="t('upsell.price-cadence')"
          variant="paragraph-small"
        />
      </div>
      <OwnType
        class="upsell-upgrade-cta__cancel"
        :text="t('upsell.cancel-any-time')"
        variant="paragraph-small"
        color="secondary"
      />
    </div>
    <OwnButton
      class="upsell-upgrade-cta__button"
      :text="t('upsell.upgrade-cta')"
      variant="primary"
      data-testid="upsell-upgrade-cta-button"
      @click="emit('upgrade')"
    />
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'

import { OwnButton } from '@/ui/OwnButton'
import { OwnType } from '@/ui/OwnType'

import { useProUpsellContent } from '../composables/useProUpsellContent'

const emit = defineEmits<{
  upgrade: []
}>()

const { t } = useI18n({ useScope: 'global' })

const { price } = useProUpsellContent()
</script>

<style lang="scss" scoped>
@use '@/styles/breakpoints';

// Pricing CTA from the tier-upgrades light design. Mobile widths get the
// full-width footer bar (Figma node 806:46646); md and up gets the floating
// island pinned to the sheet's bottom-right corner (Figma node 806:42208).
.upsell-upgrade-cta {
  width: 100%;
  padding: 12px 24px;
  border-top: 0.5px solid #cac8d9;
  background-color: var(--white);

  &__price {
    font-size: 24px;
    line-height: 32px;
  }

  &__cadence {
    font-size: 16px;
    line-height: 22px;
  }

  .upsell-upgrade-cta__button {
    min-height: 48px;
    padding: 12px 24px;
    border-radius: 16px;
    background: #0d0e0e;

    :deep(.text-button-regular) {
      font-size: 1rem;
      line-height: 20px;
    }

    &:hover {
      background-color: #1b1d1c;
    }
  }

  @include breakpoints.respond-to('md-and-up') {
    position: absolute;
    right: 24px;
    bottom: 24px;
    gap: 8px;
    width: auto;
    padding: 8px 8px 8px 16px;
    border: 0.5px solid rgb(202 200 217 / 50%);
    border-radius: 20px;
    background:
      linear-gradient(
        103deg,
        rgb(255 255 255 / 6%) 3.24%,
        rgb(86 174 221 / 9%) 49.5%,
        rgb(8 137 36 / 9%) 95.76%
      ),
      var(--white);
    box-shadow:
      0 7px 14px rgb(74 74 74 / 5%),
      0 26px 26px rgb(74 74 74 / 4%),
      0 59px 36px rgb(74 74 74 / 3%),
      0 105px 42px rgb(74 74 74 / 1%);

    &__pricing {
      min-width: 93px;
    }

    &__price {
      font-size: 16px;
      line-height: 22.4px;
    }

    &__cadence {
      font-size: 14px;
      line-height: 19.6px;
    }

    &__cancel {
      font-size: 10px;
      line-height: 14.4px;
    }

    .upsell-upgrade-cta__button {
      min-height: 45px;
      padding: 12px 16px;
      border-radius: 12px;
      box-shadow:
        0 2px 5px rgb(97 97 97 / 18%),
        0 0 0 1px rgb(73 73 73 / 70%),
        0 1px 1px #000207;

      :deep(.text-button-regular) {
        font-size: 0.875rem;
        line-height: 19.6px;
      }
    }
  }
}
</style>
